import type { CompileContext } from "../compile/context.ts";
import type { Command } from "../document/commands.ts";
import { COLLISION_CELLS_PER_TILE, type LayerDocument, type MapDocument } from "../document/types.ts";
import { foldZones } from "../document/zones.ts";
import type { ActivationCategoryStyle, NumberedActivationType } from "./activation-categories.ts";
import type { ZoneTypeDefinition } from "./adapter.ts";
import type { CollisionShape } from "./collision-authoring.ts";
import { layersContentEditedBy, layersEditedBy } from "./layer-lock.ts";
import type { ObjectTypeDefinition } from "./objects.ts";
import type { PresenceArea } from "./presence-area.ts";
import { zoneStampCommand, zoneStampOffer, type ZoneStampOffer, type ZoneStampRefusal } from "./type-stamp.ts";
import { tileRectCells, zoneLineCells, zoneStampCells } from "./zone-brush.ts";
import { numberZoneTypes, zoneAreas } from "./zone-overlay.ts";
import { paintZoneCommand } from "./zone-painting.ts";

/**
 * Zone authoring (ADR-054's amendment of 2026-09-27, decisions 4 and 5): the type in hand, painting and erasing
 * a zone layer's cells as one command, the cells a zone stroke covers, a prop's zone stamp (ADR-096 decision
 * 38), the Zones overlay's toggle beside the Activation overlay's, and the Zones overlay the map's folded zones
 * draw, remembered for the map it was drawn from so a frame asks for it without recomputing it.
 */

/**
 * What zone authoring needs from the store.
 */
export interface ZoneHost {
  readonly zoneTypes: () => readonly ZoneTypeDefinition[];
  readonly objectTypes: () => readonly ObjectTypeDefinition[];
  readonly map: () => MapDocument;
  readonly context: CompileContext;
  readonly tileSize: () => number;
  readonly activeKind: () => LayerDocument["kind"] | undefined;
  readonly activationOverlay: () => boolean;
  /** Whether an area being selected or erased across layers shows the overlay whatever its toggle says (ADR-128 decision 41c). */
  readonly areaShown: () => boolean;
  readonly setActivationOverlay: (shown: boolean) => void;
  readonly shape: () => CollisionShape;
  readonly ellipseStamp: (cx: number, cy: number, brushSize: number) => number[];
  readonly isLocked: (layerId: string) => boolean;
  readonly isHidden: (layerId: string) => boolean;
  readonly allocateId: (prefix: string) => string;
  readonly idMark: () => number;
  readonly rewindIds: (mark: number) => void;
  readonly run: (command: Command) => boolean;
  readonly notify: () => void;
}

/**
 * One connected area of the Zones overlay: its type, that type's number and style, and its cells.
 */
export interface ZoneOverlayArea {
  readonly type: string;
  readonly number: number;
  readonly style: ActivationCategoryStyle;
  readonly cells: PresenceArea;
}

/**
 * The Zones overlay of a map: every connected area of one type, and the legend, one line per painted type in
 * number order.
 */
export interface ZoneOverlay {
  readonly areas: readonly ZoneOverlayArea[];
  readonly legend: readonly NumberedActivationType[];
}

/**
 * The zone author.
 */
export class ZoneAuthoring {
  private chosen: string | null = null;
  private drawnFrom: MapDocument | null = null;
  private drawnTypes: readonly ZoneTypeDefinition[] | null = null;
  private drawn: ZoneOverlay = { areas: [], legend: [] };
  private toggle = false;

  /**
   * @param host The store's view of itself.
   */
  public constructor(private readonly host: ZoneHost) {}

  /**
   * The zone type the next zone stroke or fill paints: the chosen one, else the first registered.
   *
   * @returns The type, or null when the game registers none.
   */
  public get type(): ZoneTypeDefinition | null {
    const types = this.host.zoneTypes();
    return types.find((t) => t.id === this.chosen) ?? types[0] ?? null;
  }

  /**
   * Chooses the zone type in hand, the Zones palette's row. View state outside history.
   *
   * @param id The type's id.
   * @throws Error when the game registers no type of that id.
   */
  public setType(id: string): void {
    if (!this.host.zoneTypes().some((t) => t.id === id)) {
      throw new Error(`no zone type ${id}`);
    }
    this.chosen = id;
    this.host.notify();
  }

  /**
   * Paints cells of a zone layer with the type in hand, or erases them, as one command; a locked or hidden
   * layer takes none of it.
   *
   * @param layerId The zone layer.
   * @param indices Cell indices in the collision grid.
   * @param erase True to erase to unpainted, the right button's stroke.
   * @throws Error when the layer is not a zone layer, an index lies outside the map, or, for a paint, the game
   *   registers no zone types; an erase needs none.
   */
  public paintCells(layerId: string, indices: readonly number[], erase: boolean): boolean {
    const type = this.type;
    if (type === null && !erase) {
      throw new Error("the game registers no zone types");
    }
    const command = paintZoneCommand(this.host.map(), layerId, indices, erase || type === null ? null : type.id);
    return command === null || this.host.run(command);
  }

  /**
   * The Zones overlay of the map in view: its folded zone cells in connected areas, each in its type's
   * category, and the legend's lines. The same object until the map or the registration changes.
   *
   * @returns The overlay.
   */
  public overlay(): ZoneOverlay {
    const map = this.host.map();
    const types = this.host.zoneTypes();
    if (map === this.drawnFrom && types === this.drawnTypes) {
      return this.drawn;
    }
    const folded = foldZones(map);
    const legend = numberZoneTypes(types, folded.types);
    const byType = new Map(legend.map((line) => [line.type, line]));
    const areas = zoneAreas(folded, map.width * COLLISION_CELLS_PER_TILE, map.height * COLLISION_CELLS_PER_TILE).map((area) => {
      const line = byType.get(area.type) as NumberedActivationType;
      return { type: area.type, number: line.number, style: line.style, cells: area.cells };
    });
    this.drawnFrom = map;
    this.drawnTypes = types;
    this.drawn = { areas, legend };
    return this.drawn;
  }

  /**
   * Whether the Zones overlay's toggle (U) is on. View state outside history.
   *
   * @returns True while on.
   */
  public get overlayToggle(): boolean {
    return this.toggle;
  }

  /**
   * Turns the Zones overlay's toggle on or off; turning it on turns the Activation overlay off, since the two
   * share the categories and are never shown at once (ADR-054's amendment of 2026-09-27, decision 5).
   *
   * @param shown Whether on.
   */
  public setOverlayToggle(shown: boolean): void {
    if (shown) {
      this.host.setActivationOverlay(false);
    }
    if (shown !== this.toggle) {
      this.toggle = shown;
      this.host.notify();
    }
  }

  /**
   * Turns the Activation overlay on or off; turning it on turns the Zones overlay's toggle off.
   *
   * @param shown Whether on.
   */
  public setActivationOverlay(shown: boolean): void {
    if (shown) {
      this.setOverlayToggle(false);
    }
    this.host.setActivationOverlay(shown);
  }

  /**
   * Whether the map draws the Zones overlay: in Zones mode, while its toggle is on and while an area across layers
   * stands or is drawn (ADR-128 decision 41c), for a game that registers zone types.
   *
   * @returns True while drawn.
   */
  public get shown(): boolean {
    if (this.host.zoneTypes().length === 0) {
      return false;
    }
    return this.toggle || this.host.activeKind() === "zone" || this.host.areaShown();
  }

  /**
   * Whether the map draws the Activation overlay: while its toggle is on and the Zones overlay is not drawn.
   *
   * @returns True while drawn.
   */
  public get activationShown(): boolean {
    return this.host.activationOverlay() && !this.shown;
  }

  /**
   * The collision cells one zone stamp of the current shape covers at a cell (ADR-054's amendment of
   * 2026-10-01, decision 1): every shape but the Ellipse stamps the brush size's square of whole tiles around
   * the tile holding the cell; the Ellipse keeps its extents in cells.
   *
   * @param cx Cell column in the collision grid.
   * @param cy Cell row.
   * @param brushSize Brush size in tiles.
   * @returns Cell indices.
   */
  public stamp(cx: number, cy: number, brushSize: number): number[] {
    if (this.host.shape() === "ellipse") {
      return this.host.ellipseStamp(cx, cy, brushSize);
    }
    const map = this.host.map();
    return zoneStampCells(cx, cy, brushSize, map.width, map.height);
  }

  /**
   * The collision cells a zone drag covers: the whole tiles between the two cells' tiles for the Rectangle, a
   * line of the brush size's square of tiles from tile to tile for the Line, and nothing for stamp shapes.
   *
   * @param x0 Start cell column in the collision grid.
   * @param y0 Start cell row.
   * @param x1 End cell column.
   * @param y1 End cell row.
   * @param brushSize Brush size in tiles, the line's thickness.
   * @returns Cell indices.
   */
  public span(x0: number, y0: number, x1: number, y1: number, brushSize: number): number[] {
    const { width, height } = this.host.map();
    switch (this.host.shape()) {
      case "rect":
        return tileRectCells(Math.floor(x0 / COLLISION_CELLS_PER_TILE), Math.floor(y0 / COLLISION_CELLS_PER_TILE), Math.floor(x1 / COLLISION_CELLS_PER_TILE), Math.floor(y1 / COLLISION_CELLS_PER_TILE), width, height);
      case "line":
        return zoneLineCells(x0, y0, x1, y1, brushSize, width, height);
      default:
        return [];
    }
  }

  /**
   * What a prop's type offers to paint around it (ADR-096 decision 38): the zone type of its stamp and whether the
   * command also writes the prop's activation cells.
   *
   * @param layerId Prop layer id.
   * @param propId Prop id.
   * @returns The offer, or null for a missing prop, one with no behaviour, or a type that declares no stamp.
   */
  public propStamp(layerId: string, propId: string): ZoneStampOffer | null {
    const layer = this.host.map().layers.find((candidate) => candidate.id === layerId);
    return zoneStampOffer(layer?.kind === "prop" ? layer.props.find((prop) => prop.id === propId) : undefined, this.host.objectTypes(), this.host.zoneTypes());
  }

  /**
   * Paints a prop's zone stamp as one command (`zoneStampCommand`, ADR-096 decision 38): the stamp's mask around the
   * prop on the topmost zone layer holding its zone type, or on a new one named for the type, and, when the stamp
   * says so, the same cells as the prop's activation. Painting what is already there records nothing.
   *
   * @param layerId Prop layer id.
   * @param propId Prop id.
   * @returns Null when the zone is painted or was so already; otherwise why nothing was painted, as a code: `locked`
   *   for a layer the command would change that is locked or hidden, `refused` when the store's session took no
   *   command, which the session tells its host itself. A press that adds no layer spends no layer id.
   * @throws Error when the prop does not exist.
   */
  public paintPropStamp(layerId: string, propId: string): ZoneStampRefusal | null {
    const mark = this.host.idMark();
    const made = zoneStampCommand(this.host.map(), layerId, propId, { types: this.host.objectTypes(), zoneTypes: this.host.zoneTypes(), context: this.host.context, tileSize: this.host.tileSize(), newLayerId: () => this.host.allocateId("layer") });
    const command = typeof made === "string" ? null : made.command;
    const locked = command !== null && (layersEditedBy(command).some((id) => this.host.isLocked(id)) || layersContentEditedBy(command).some((id) => this.host.isHidden(id)));
    if (command !== null && !locked && this.host.run(command)) {
      return null;
    }
    this.host.rewindIds(mark);
    if (typeof made === "string") {
      return made;
    }
    return command === null ? null : locked ? "locked" : "refused";
  }
}
